import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Select, Space, Table, Tag, Typography, message } from 'antd';
import { SlidersOutlined } from '@ant-design/icons';
import API_BASE_URL, { ENDPOINTS } from '../../helpers/api/apiConfig';
import MetricRuleEditorModal from '../MetricRules/MetricRuleEditorModal';
import {
  MetricRuleSet,
  buildConfigurationsWithAreaTypeMetricRule,
  cleanBenchmarkBandRangeMap,
  cleanMetricRangeMap,
  getAreaTypeMetricRuleSetForKey,
  getMetricRuleDefaultsFromMetric,
  isMetricRuleSetEmpty,
} from '../../helpers/metricRules';

const { Text } = Typography;

interface AreaTypeOption {
  id: number;
  name: string;
}

interface RangesTabProps {
  isAdmin: boolean;
  companyId?: number;
  buildAuthHeaders: (includeContentType?: boolean) => Record<string, string>;
  onAuthMismatch: (response: Response, errorData: any) => Promise<boolean>;
}

const RangesTab: React.FC<RangesTabProps> = ({
  isAdmin,
  companyId,
  buildAuthHeaders,
  onAuthMismatch,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [companyInfo, setCompanyInfo] = useState<any>(null);
  const [areaTypes, setAreaTypes] = useState<AreaTypeOption[]>([]);
  const [metrics, setMetrics] = useState<any[]>([]);
  const [selectedAreaTypeId, setSelectedAreaTypeId] = useState<number | null>(null);
  const [editingMetric, setEditingMetric] = useState<any | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);

  const fetchData = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const [companyRes, metaRes] = await Promise.all([
        fetch(`${API_BASE_URL}/companies/${companyId}`, {
          headers: {
            ...buildAuthHeaders(),
          },
          credentials: 'include',
        }),
        fetch(ENDPOINTS.REPORT_TEMPLATE_WIZARD_META, {
          headers: {
            ...buildAuthHeaders(),
          },
          credentials: 'include',
        }),
      ]);

      if (!companyRes.ok) {
        const errorData = await companyRes.json();
        if (await onAuthMismatch(companyRes, errorData)) return;
        message.error(errorData?.message || 'Failed to load company settings');
        return;
      }
      if (!metaRes.ok) {
        const errorData = await metaRes.json();
        if (await onAuthMismatch(metaRes, errorData)) return;
        message.error(errorData?.message || 'Failed to load metrics and area types');
        return;
      }

      const companyData = await companyRes.json();
      const metaDataRaw = await metaRes.json();
      const metaData = metaDataRaw?.data || metaDataRaw || {};

      const nextAreaTypes: AreaTypeOption[] = (metaData.areaTypes || []).map((item: any) => ({
        id: item.id,
        name: item.name,
      }));

      setCompanyInfo(companyData || null);
      setAreaTypes(nextAreaTypes);
      setMetrics(metaData.metrics || []);
      if (!selectedAreaTypeId && nextAreaTypes.length > 0) {
        setSelectedAreaTypeId(nextAreaTypes[0].id);
      }
    } catch (error) {
      console.error('Failed to load ranges settings', error);
      message.error('Failed to load ranges settings');
    } finally {
      setLoading(false);
    }
  }, [buildAuthHeaders, companyId, onAuthMismatch, selectedAreaTypeId]);

  useEffect(() => {
    if (!isAdmin) return;
    fetchData();
  }, [isAdmin, fetchData]);

  const openEditor = (metric: any) => {
    setEditingMetric(metric);
    setEditorOpen(true);
  };

  const handleSave = async (nextRuleSet: MetricRuleSet | null) => {
    if (!editingMetric || !selectedAreaTypeId || !companyId) return;
    try {
      const nextConfigurations = buildConfigurationsWithAreaTypeMetricRule(
        companyInfo?.configurations || {},
        selectedAreaTypeId,
        editingMetric.key,
        nextRuleSet
      );

      setSaving(true);
      const response = await fetch(`${API_BASE_URL}/companies/${companyId}/configurations`, {
        method: 'PATCH',
        headers: {
          ...buildAuthHeaders(),
        },
        credentials: 'include',
        body: JSON.stringify({ configurations: nextConfigurations }),
      });

      if (response.ok) {
        setCompanyInfo((prev: any) => ({
          ...(prev || {}),
          configurations: nextConfigurations,
        }));
        setEditorOpen(false);
        setEditingMetric(null);
        message.success('Ranges updated');
      } else {
        const errorData = await response.json();
        if (await onAuthMismatch(response, errorData)) return;
        message.error(errorData?.message || 'Failed to update ranges');
      }
    } catch (error) {
      console.error('Failed to save ranges', error);
      message.error('Failed to save ranges');
    } finally {
      setSaving(false);
    }
  };

  const editingRuleSet = useMemo(() => {
    if (!editingMetric || !selectedAreaTypeId) return null;
    const current = getAreaTypeMetricRuleSetForKey(
      companyInfo?.configurations,
      selectedAreaTypeId,
      editingMetric.key
    );
    if (current && !isMetricRuleSetEmpty(current)) {
      return current;
    }

    return null;
  }, [companyInfo, editingMetric, selectedAreaTypeId]);

  const editingDefaults = useMemo(() => {
    if (!editingMetric) return null;
    if (selectedAreaTypeId) {
      const resolved = editingMetric?.areaTypeRuleDefaults?.[String(selectedAreaTypeId)];
      if (resolved) {
        return {
          warningRange: cleanMetricRangeMap(resolved.warningRanges),
          colorRange: cleanMetricRangeMap(resolved.colorRanges),
          notificationRange: cleanMetricRangeMap(resolved.notificationRanges),
          healthStatusRange: cleanMetricRangeMap(resolved.healthStatusRanges),
          benchmarkRange: cleanBenchmarkBandRangeMap(resolved.benchmarkRanges),
          notificationLogic: resolved.notificationLogic || undefined,
          healthStatusLogic: resolved.healthStatusLogic || undefined,
        } as MetricRuleSet;
      }
    }
    return getMetricRuleDefaultsFromMetric(editingMetric);
  }, [editingMetric, selectedAreaTypeId]);

  const metricRuleSummary = useCallback((metricKey: string) => {
    if (!selectedAreaTypeId) return [];
    const current = getAreaTypeMetricRuleSetForKey(
      companyInfo?.configurations,
      selectedAreaTypeId,
      metricKey
    );
    const tags: string[] = [];

    if (current?.warningRange && Object.keys(current.warningRange).length > 0) tags.push('warning');
    if (current?.colorRange && Object.keys(current.colorRange).length > 0) tags.push('color');
    if (current?.notificationRange && Object.keys(current.notificationRange).length > 0) tags.push('notification');
    if (current?.healthStatusRange && Object.keys(current.healthStatusRange).length > 0) tags.push('health');
    if (current?.benchmarkRange && Object.keys(current.benchmarkRange).length > 0) tags.push('benchmark');
    if (current?.notificationLogic) tags.push('notification logic');
    if (current?.healthStatusLogic) tags.push('health logic');

    return tags;
  }, [companyInfo, selectedAreaTypeId]);

  const columns = useMemo(() => [
    {
      title: 'Metric',
      dataIndex: 'name',
      key: 'name',
      render: (_: any, record: any) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.name || record.key}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.key}</Text>
        </Space>
      ),
    },
    {
      title: 'Overrides',
      key: 'overrides',
      render: (_: any, record: any) => {
        const tags = metricRuleSummary(record.key);
        if (tags.length === 0) {
          return <Text type="secondary">—</Text>;
        }
        return (
          <Space wrap>
            {tags.map((label) => (
              <Tag key={`${record.key}-${label}`}>{label}</Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Button disabled={!selectedAreaTypeId} onClick={() => openEditor(record)}>
          Edit
        </Button>
      ),
    },
  ], [metricRuleSummary, selectedAreaTypeId]);

  if (!isAdmin) {
    return (
      <Alert
        message="Access Restricted"
        description="Only administrators can configure metric ranges."
        type="warning"
        showIcon
      />
    );
  }

  if (!companyId) {
    return (
      <Alert
        message="No Company Selected"
        description="Select a company to configure area type ranges."
        type="info"
        showIcon
      />
    );
  }

  return (
    <Card
      title={
        <Space>
          <SlidersOutlined />
          Ranges
        </Space>
      }
      loading={loading}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <div>
          <Text strong>Company</Text>
          <div style={{ marginTop: 4 }}>
            <Space direction="vertical" size={0}>
              <Text>{companyInfo?.name || `Company ${companyId}`}</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Company ID: {companyId}
              </Text>
            </Space>
          </div>
        </div>

        <div>
          <Text strong>Area Type</Text>
          <div style={{ marginTop: 8, maxWidth: 420 }}>
            <Select
              value={selectedAreaTypeId ?? undefined}
              onChange={(value) => setSelectedAreaTypeId(value)}
              style={{ width: '100%' }}
              placeholder="Select area type"
              options={areaTypes.map((areaType) => ({
                label: areaType.name,
                value: areaType.id,
              }))}
            />
          </div>
        </div>

        <Table
          rowKey={(record) => record.id || record.key}
          columns={columns}
          dataSource={metrics}
          pagination={{ pageSize: 8 }}
          locale={{ emptyText: 'No metrics found.' }}
        />
      </Space>

      <MetricRuleEditorModal
        open={editorOpen}
        metric={editingMetric}
        initialRuleSet={editingRuleSet}
        defaults={editingDefaults}
        saving={saving}
        onCancel={() => {
          setEditorOpen(false);
          setEditingMetric(null);
        }}
        onSave={handleSave}
      />
    </Card>
  );
};

export default RangesTab;
